// packages/core/mesh/rpc-tunnel/src/ports/iframe.ts
import { log } from "@dxos/log";
var sendToIFrame = (iframe, origin, message) => {
  if (!iframe.contentWindow) {
    log("IFrame content window missing", {
      origin
    }, {
      file: "iframe.ts",
      line: 12,
      scope: void 0,
      callSite: (f, a) => f(...a)
    });
    return;
  }
  iframe.contentWindow.postMessage(message, "*", [
    message.payload
  ]);
};
var sendToParentWindow = (origin, message) => {
  window.parent.postMessage(message, origin, [
    message.payload
  ]);
};
var createIFramePort = ({ channel, iframe, origin, onOrigin }) => {
  return {
    send: async (data) => {
      if (!origin) {
        log.warn("no origin set", {
          channel
        }, {
          file: "iframe.ts",
          line: 43,
          scope: void 0,
          callSite: (f, a) => f(...a)
        });
        return;
      }
      log("sending", {
        channel,
        data: data.length
      }, {
        file: "iframe.ts",
        line: 47,
        scope: void 0,
        callSite: (f, a) => f(...a)
      });
      const payload = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
      const message = {
        channel,
        payload
      };
      if (iframe) {
        sendToIFrame(iframe, origin, message);
      } else {
        sendToParentWindow(origin, message);
      }
    },
    subscribe: (callback) => {
      const handler = (event) => {
        if (!iframe && event.source !== window.parent) {
          return;
        } else if (iframe && event.source !== iframe.contentWindow) {
          return;
        }
        const isMessageData = event.data && typeof event.data === "object" && "channel" in event.data && "payload" in event.data;
        const message = isMessageData ? event.data : void 0;
        if ((message == null ? void 0 : message.channel) !== channel) {
          return;
        }
        if (!origin) {
          origin = event.origin;
          onOrigin == null ? void 0 : onOrigin(origin);
        }
        log("received", message, {
          file: "iframe.ts",
          line: 79,
          scope: void 0,
          callSite: (f, a) => f(...a)
        });
        callback(new Uint8Array(message.payload));
      };
      window.addEventListener("message", handler);
      return () => window.removeEventListener("message", handler);
    }
  };
};
var createIFrame = (source, id, { hidden = true, allow } = {}) => {
  var _a;
  const create = () => {
    const iframe = document.createElement("iframe");
    iframe.id = id;
    iframe.src = source;
    hidden && iframe.setAttribute("style", "display: none;");
    allow && iframe.setAttribute("allow", allow);
    document.body.appendChild(iframe);
    return iframe;
  };
  {
    const cssStyle = "color:#C026D3;font-weight:bold";
    console.log(`%cDXOS Client is communicating with the shared worker on ${source}.
Inspect the worker using: chrome://inspect/#workers (URL must be copied manually).`, cssStyle);
    console.log(`%cTo inspect this application, click here:
https://devtools.dxos.org/?target=vault:${source}`, cssStyle);
  }
  return (_a = document.getElementById(id)) != null ? _a : create();
};

// packages/core/mesh/rpc-tunnel/src/ports/worker.ts
import { log as log2 } from "@dxos/log";
var createWorkerPort = ({ port, channel, subscribe }) => ({
  send: async (message) => {
    const payload = message.buffer.slice(message.byteOffset, message.byteOffset + message.byteLength);
    port.postMessage({
      channel,
      payload
    }, [
      payload
    ]);
  },
  subscribe: subscribe != null ? subscribe : (callback) => {
    const handler = (event) => {
      const message = event.data;
      if (message.channel !== channel) {
        return;
      }
      log2.debug("Recieved message", message, {
        file: "worker.ts",
        line: 45,
        scope: void 0,
        callSite: (f, a) => f(...a)
      });
      callback(new Uint8Array(message.payload));
    };
    port.onmessage = handler;
    return () => {
      port.onmessage = null;
    };
  }
});

// packages/core/mesh/rpc-tunnel/src/port-muxer.ts
import { log as log3 } from "@dxos/log";
var PortMuxer = class {
  // prettier-ignore
  constructor(_messagePort) {
    this._messagePort = _messagePort;
    this._activeChannels = /* @__PURE__ */ new Map();
    this._rpcPorts = /* @__PURE__ */ new Map();
    if (this._messagePort) {
      this._messagePort.onmessage = (event) => this.onWorkerMessage(event);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("message", (event) => this.onWindowMessage(event));
    }
  }
  createWorkerPort(options) {
    if (!this._messagePort) {
      throw new Error("Message port is required to create worker ports");
    }
    const port = createWorkerPort({
      ...options,
      port: this._messagePort,
      subscribe: (callback) => {
        this._activeChannels.set(options.channel, callback);
        return () => this._activeChannels.delete(options.channel);
      }
    });
    this._rpcPorts.set(options.channel, port);
    return port;
  }
  createIFramePort(options) {
    const port = createIFramePort(options);
    this._rpcPorts.set(options.channel, port);
    return port;
  }
  onWorkerMessage(event) {
    const message = event.data;
    log3.debug("Recieved message from worker port", {
      channel: message.channel,
      payload: message.payload
    }, {
      file: "port-muxer.ts",
      line: 59,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    const callback = this._activeChannels.get(message.channel);
    callback == null ? void 0 : callback(new Uint8Array(message.payload));
  }
  onWindowMessage(event) {
    const message = event.data;
    log3.debug("Recieved message from window", {
      channel: message.channel,
      payload: message.payload
    }, {
      file: "port-muxer.ts",
      line: 70,
      scope: this,
      callSite: (f, a) => f(...a)
    });
  }
};
export {
  PortMuxer,
  createIFrame,
  createIFramePort,
  createWorkerPort
};
//# sourceMappingURL=index.mjs.map
